import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  ShieldCheck, 
  ShieldAlert, 
  UserCheck, 
  UserX, 
  Search, 
  Filter, 
  RotateCcw, 
  Edit3, 
  Trash2, 
  AlertTriangle, 
  CheckCircle2, 
  X, 
  Lock, 
  Clock, 
  Calendar,
  Shield,
  KeyRound,
  RefreshCw,
  MoreVertical,
  UserPlus,
  Phone
} from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { 
  getAdminUsers, 
  createAdminUser,
  updateUserRole, 
  updateUserStatus, 
  updateUserDetails, 
  deleteUser 
} from '../api/admin';

const ROLES = [
  { key: 'SUPER_ADMIN', name: 'Super Admin', desc: 'Unrestricted control over system, users, and audit logs', color: '#8b5cf6' },
  { key: 'ADMIN', name: 'Admin', desc: 'Can manage users, policies, devices, and sessions', color: '#3b82f6' },
  { key: 'SECURITY_ADMIN', name: 'Security Admin', desc: 'Manages IP security, policies, and system safeguards', color: '#06b6d4' },
  { key: 'AUDITOR', name: 'Auditor', desc: 'Read-only access to compliance records and audit logs', color: '#10b981' },
  { key: 'SUPPORT', name: 'Support', desc: 'Assists with device and session management', color: '#f59e0b' },
  { key: 'USER', name: 'User', desc: 'Standard encrypted vault owner', color: '#64748b' },
];

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Edit modal state
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('USER');
  const [editIsActive, setEditIsActive] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete modal state
  const [deletingUser, setDeletingUser] = useState(null);
  const [savingDelete, setSavingDelete] = useState(false);

  // Create user modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState('USER');
  const [savingCreate, setSavingCreate] = useState(false);
  const [createError, setCreateError] = useState('');

  // Toast / feedback message
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadUsers = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await getAdminUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const query = searchQuery.toLowerCase().trim();
      const matchesQuery = !query || 
        u.email?.toLowerCase().includes(query) || 
        u.id?.toLowerCase().includes(query);

      const matchesRole = roleFilter === 'ALL' || u.role === roleFilter;
      const matchesStatus = statusFilter === 'ALL' || 
        (statusFilter === 'ACTIVE' && u.is_active) || 
        (statusFilter === 'SUSPENDED' && !u.is_active);

      return matchesQuery && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active).length;
    const suspended = users.filter((u) => !u.is_active).length;
    const admins = users.filter((u) => ['SUPER_ADMIN', 'ADMIN'].includes(u.role)).length;
    return { total, active, suspended, admins };
  }, [users]);

  // Handle quick suspend / activate toggle
  const handleToggleStatus = async (userToToggle) => {
    if (userToToggle.id === currentUser?.id) {
      showToast('You cannot suspend your own admin account.', 'error');
      return;
    }

    const nextStatus = !userToToggle.is_active;
    try {
      await updateUserStatus(userToToggle.id, nextStatus);
      setUsers((prev) => 
        prev.map((u) => u.id === userToToggle.id ? { ...u, is_active: nextStatus, status: nextStatus ? 'ACTIVE' : 'SUSPENDED' } : u)
      );
      showToast(
        nextStatus 
          ? `Account ${userToToggle.email} has been reactivated.` 
          : `Account ${userToToggle.email} has been suspended. Active sessions were revoked immediately.`,
        nextStatus ? 'success' : 'warning'
      );
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update user status', 'error');
    }
  };

  // Create User Handler
  const handleCreateUserSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = createEmail.trim().toLowerCase();
    const cleanPhone = createPhone.trim();
    if (!cleanEmail) {
      setCreateError('Email address is required.');
      return;
    }
    if (!cleanPhone) {
      setCreateError('Phone number is compulsory.');
      return;
    }
    if (cleanPhone.replace(/\D/g, '').length < 7) {
      setCreateError('Please enter a valid phone number (at least 7 digits).');
      return;
    }

    setSavingCreate(true);
    setCreateError('');
    try {
      await createAdminUser({
        email: cleanEmail,
        phone: cleanPhone,
        role: createRole,
        password: createPassword.trim() || 'CipherVault@2026',
      });
      showToast(`User ${cleanEmail} created successfully in database!`, 'success');
      setIsCreateModalOpen(false);
      setCreateEmail('');
      setCreatePhone('');
      setCreatePassword('');
      setCreateRole('USER');
      setCreateError('');
      loadUsers();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create user in database.');
    } finally {
      setSavingCreate(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditRole(userToEdit.role || 'USER');
    setEditIsActive(userToEdit.is_active !== false);
  };

  // Save Edit Modal
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    if (editingUser.id === currentUser?.id && !editIsActive) {
      showToast('You cannot suspend your own account.', 'error');
      return;
    }
    if (editingUser.id === currentUser?.id && editRole !== currentUser?.role) {
      showToast('You cannot alter your own admin role.', 'error');
      return;
    }

    setSavingEdit(true);
    try {
      await updateUserDetails(editingUser.id, {
        role: editRole,
        is_active: editIsActive,
      });

      setUsers((prev) => 
        prev.map((u) => u.id === editingUser.id ? { 
          ...u, 
          role: editRole, 
          is_active: editIsActive,
          status: editIsActive ? 'ACTIVE' : 'SUSPENDED'
        } : u)
      );

      showToast(`User ${editingUser.email} updated successfully.`);
      setEditingUser(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save changes', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // Open Delete Modal
  const openDeleteModal = (userToDelete) => {
    if (userToDelete.id === currentUser?.id) {
      showToast('You cannot delete your own admin account.', 'error');
      return;
    }
    setDeletingUser(userToDelete);
  };

  // Execute Delete
  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    setSavingDelete(true);
    try {
      await deleteUser(deletingUser.id);
      setUsers((prev) => prev.filter((u) => u.id !== deletingUser.id));
      showToast(`User ${deletingUser.email} and all associated vaults were permanently deleted.`);
      setDeletingUser(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete user', 'error');
    } finally {
      setSavingDelete(false);
    }
  };

  // Helper for role badge colors
  const getRoleMeta = (roleKey) => {
    return ROLES.find((r) => r.key === roleKey) || { name: roleKey, color: '#64748b' };
  };

  // Format date helper
  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <MainLayout>
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.type === 'error' && <AlertTriangle size={18} />}
          {toast.type === 'warning' && <ShieldAlert size={18} />}
          {toast.type === 'success' && <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="toast-close-btn" aria-label="Close message">
            <X size={15} />
          </button>
        </div>
      )}

      {/* Page Header - Full Bleed Edge-to-Edge */}
      <div className="admin-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="admin-header-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="admin-header-icon-badge notes-hero-icon-badge" style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}>
              <Users size={24} />
            </div>
            <div>
              <h1 className="notes-hero-title">User Management</h1>
              <p className="notes-hero-subtitle">Full administrative authority over accounts, security roles, suspensions, and access rights.</p>
            </div>
          </div>
          <div className="admin-header-actions" style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn-primary notes-hero-btn-primary"
              onClick={() => {
                setCreateEmail('');
                setCreatePhone('');
                setCreatePassword('');
                setCreateRole('USER');
                setCreateError('');
                setIsCreateModalOpen(true);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#10b981', borderColor: '#10b981' }}
            >
              <UserPlus size={16} />
              <span>Create User</span>
            </button>
            <button 
              className={`btn-secondary notes-hero-btn-secondary ${refreshing ? 'spinning' : ''}`}
              onClick={() => loadUsers(true)}
              disabled={refreshing || loading}
              title="Refresh users list"
            >
              <RefreshCw size={16} className={refreshing ? 'rotate-anim' : ''} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-page-container">
        {/* Statistics Cards */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
              <Users size={22} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Total Users</span>
              <strong className="admin-stat-value">{stats.total}</strong>
            </div>
          </div>

          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
              <UserCheck size={22} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Active Users</span>
              <strong className="admin-stat-value">{stats.active}</strong>
            </div>
          </div>

          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
              <UserX size={22} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Suspended</span>
              <strong className="admin-stat-value">{stats.suspended}</strong>
            </div>
          </div>

          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
              <ShieldCheck size={22} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Administrators</span>
              <strong className="admin-stat-value">{stats.admins}</strong>
            </div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="admin-toolbar">
          <div className="admin-search-wrapper">
            <Search size={18} className="admin-search-icon" />
            <input 
              type="text" 
              placeholder="Search by user email or identifier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button 
                className="admin-search-clear" 
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="admin-filter-group">
            <div className="admin-select-wrapper">
              <Filter size={15} className="admin-filter-icon" />
              <select 
                value={roleFilter} 
                onChange={(e) => setRoleFilter(e.target.value)}
                className="admin-select"
                aria-label="Filter by Role"
              >
                <option value="ALL">All Roles</option>
                {ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </select>
            </div>

            <div className="admin-select-wrapper">
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-select"
                aria-label="Filter by Status"
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active Only</option>
                <option value="SUSPENDED">Suspended Only</option>
              </select>
            </div>

            {(searchQuery || roleFilter !== 'ALL' || statusFilter !== 'ALL') && (
              <button 
                className="btn-ghost btn-sm"
                onClick={() => {
                  setSearchQuery('');
                  setRoleFilter('ALL');
                  setStatusFilter('ALL');
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Users Table / List */}
        <div className="admin-table-container glass-panel">
          {loading ? (
            <div className="admin-loading-state">
              <RefreshCw size={32} className="rotate-anim" style={{ color: 'var(--accent-primary)', marginBottom: '12px' }} />
              <p>Loading registered accounts...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="admin-empty-state">
              <Users size={44} style={{ opacity: 0.35, marginBottom: '16px' }} />
              <h3>No users found</h3>
              <p>
                {searchQuery || roleFilter !== 'ALL' || statusFilter !== 'ALL'
                  ? 'No users match your current search and filter criteria.'
                  : 'There are no users registered on the platform yet.'}
              </p>
            </div>
          ) : (
            <div className="admin-table-scroll">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Security Role</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const roleMeta = getRoleMeta(u.role);
                    const isSelf = u.id === currentUser?.id;
                    const isSuspended = !u.is_active;

                    return (
                      <tr key={u.id} className={isSuspended ? 'row-suspended' : ''}>
                        {/* User Email & Avatar */}
                        <td>
                          <div className="admin-user-cell">
                            <div className="admin-user-avatar">
                              {u.email ? u.email[0].toUpperCase() : 'U'}
                            </div>
                            <div className="admin-user-info">
                              <div className="admin-user-email-row">
                                <strong className="admin-user-email">{u.email}</strong>
                                {isSelf && (
                                  <span className="admin-self-pill">You</span>
                                )}
                              </div>
                              <span className="admin-user-id" title={u.id}>
                                {u.phone ? `📱 ${u.phone} • ` : ''}ID: {u.id.slice(0, 8)}...
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Role Badge */}
                        <td>
                          <span 
                            className="admin-role-badge"
                            style={{
                              backgroundColor: `${roleMeta.color}18`,
                              borderColor: `${roleMeta.color}40`,
                              color: roleMeta.color,
                            }}
                          >
                            <Shield size={12} style={{ marginRight: 5 }} />
                            {roleMeta.name}
                          </span>
                        </td>

                        {/* Status Badge */}
                        <td>
                          {isSuspended ? (
                            <span className="admin-status-pill status-suspended" title="This user cannot log in and has no active sessions">
                              <span className="status-dot"></span>
                              Suspended
                            </span>
                          ) : (
                            <span className="admin-status-pill status-active" title="User account is fully active">
                              <span className="status-dot"></span>
                              Active
                            </span>
                          )}
                        </td>

                        {/* Registered Date */}
                        <td>
                          <div className="admin-date-cell">
                            <Calendar size={13} style={{ opacity: 0.6, marginRight: 5 }} />
                            <span>{formatDate(u.created_at)}</span>
                          </div>
                        </td>

                        {/* Action Buttons */}
                        <td style={{ textAlign: 'right' }}>
                          <div className="admin-actions-cell">
                            {/* Edit Button */}
                            <button 
                              className="admin-action-btn btn-edit"
                              onClick={() => openEditModal(u)}
                              title="Edit user role and details"
                            >
                              <Edit3 size={15} />
                              <span className="btn-label">Edit</span>
                            </button>

                            {/* Suspend / Reactivate Button */}
                            <button 
                              className={`admin-action-btn ${isSuspended ? 'btn-activate' : 'btn-suspend'}`}
                              onClick={() => handleToggleStatus(u)}
                              disabled={isSelf}
                              title={
                                isSelf 
                                  ? 'You cannot suspend your own account' 
                                  : isSuspended 
                                    ? 'Reactivate user account' 
                                    : 'Suspend user account and revoke all sessions'
                              }
                            >
                              {isSuspended ? <UserCheck size={15} /> : <UserX size={15} />}
                              <span className="btn-label">{isSuspended ? 'Reactivate' : 'Suspend'}</span>
                            </button>

                            {/* Delete Button */}
                            <button 
                              className="admin-action-btn btn-delete"
                              onClick={() => openDeleteModal(u)}
                              disabled={isSelf}
                              title={isSelf ? 'You cannot delete your own account' : 'Permanently delete user'}
                            >
                              <Trash2 size={15} />
                              <span className="btn-label">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {isCreateModalOpen && (
          <div className="modal-backdrop" onClick={() => !savingCreate && setIsCreateModalOpen(false)}>
            <div className="modal-card glass-panel" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                  <UserPlus size={20} />
                </div>
                <div>
                  <h3>Create New User</h3>
                  <p>Provision a new account with compulsory phone number and security role.</p>
                </div>
                <button className="modal-close" onClick={() => setIsCreateModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              {createError && (
                <div className="admin-alert-banner danger" style={{ margin: '14px 20px 0 20px' }}>
                  <AlertTriangle size={16} />
                  <span>{createError}</span>
                </div>
              )}

              <form onSubmit={handleCreateUserSubmit} className="admin-modal-form">
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    className="input-field"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number * (Compulsory)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="tel"
                      placeholder="e.g. +91 9800000000"
                      value={createPhone}
                      onChange={(e) => setCreatePhone(e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Temporary Master Password (Optional - defaults to CipherVault@2026)</label>
                  <input
                    type="password"
                    placeholder="Enter password or leave blank for default"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    className="input-field"
                  />
                </div>

                <div className="form-group">
                  <label>Assign Security Role</label>
                  <select
                    value={createRole}
                    onChange={(e) => setCreateRole(e.target.value)}
                    className="input-field"
                  >
                    {ROLES.map((r) => (
                      <option key={r.key} value={r.key}>{r.name} ({r.desc.slice(0, 40)}...)</option>
                    ))}
                  </select>
                </div>

                <div className="modal-footer" style={{ marginTop: '20px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setIsCreateModalOpen(false)}
                    disabled={savingCreate}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingCreate || !createEmail.trim() || !createPhone.trim()}
                    style={{ background: '#10b981', borderColor: '#10b981' }}
                  >
                    {savingCreate ? 'Creating in Database...' : 'Create User in DB'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
            <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-icon">
                  <Edit3 size={20} />
                </div>
                <div>
                  <h3>Edit User</h3>
                  <p>Update role permissions and account status for <strong>{editingUser.email}</strong></p>
                </div>
                <button className="modal-close" onClick={() => setEditingUser(null)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveEdit} className="admin-modal-form">
                {/* Email (Readonly) */}
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="text" 
                    value={editingUser.email} 
                    disabled 
                    className="input-field disabled" 
                  />
                </div>

                {/* Role Selector */}
                <div className="form-group">
                  <label>Security Role</label>
                  <div className="admin-role-options">
                    {ROLES.map((r) => (
                      <label 
                        key={r.key} 
                        className={`admin-role-card ${editRole === r.key ? 'selected' : ''}`}
                        style={{
                          borderColor: editRole === r.key ? r.color : 'transparent',
                        }}
                      >
                        <input 
                          type="radio" 
                          name="role" 
                          value={r.key}
                          checked={editRole === r.key}
                          onChange={(e) => setEditRole(e.target.value)}
                        />
                        <div className="role-card-content">
                          <div className="role-card-header">
                            <span className="role-card-name" style={{ color: r.color }}>{r.name}</span>
                          </div>
                          <p className="role-card-desc">{r.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="form-group">
                  <label>Account Access Status</label>
                  <div className="admin-status-toggle-card">
                    <div className="status-toggle-info">
                      <strong>{editIsActive ? 'Account is Active' : 'Account is Suspended'}</strong>
                      <p>
                        {editIsActive 
                          ? 'The user has permission to log in and access their vaults.' 
                          : 'The user cannot log in to any panel, and all current sessions are invalidated immediately.'}
                      </p>
                    </div>
                    <label className="switch-toggle" title="Toggle account status">
                      <input 
                        type="checkbox" 
                        checked={editIsActive} 
                        disabled={editingUser.id === currentUser?.id}
                        onChange={(e) => setEditIsActive(e.target.checked)} 
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  {editingUser.id === currentUser?.id && (
                    <small style={{ color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      * You cannot suspend your own logged-in administrator account.
                    </small>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setEditingUser(null)}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Saving Changes...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="modal-backdrop" onClick={() => setDeletingUser(null)}>
            <div className="modal-card modal-danger glass-panel" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-icon danger-icon">
                  <AlertTriangle size={22} />
                </div>
                <div>
                  <h3>Permanently Delete User</h3>
                  <p>This action cannot be undone.</p>
                </div>
                <button className="modal-close" onClick={() => setDeletingUser(null)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body-content">
                <p>
                  Are you sure you want to permanently delete account: <br />
                  <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>{deletingUser.email}</strong>?
                </p>
                <div className="delete-warning-box">
                  <AlertTriangle size={18} className="delete-warning-icon" />
                  <div>
                    <strong>Warning: Full Cascade Deletion</strong>
                    <p>
                      All encrypted vaults, passwords, shared items, devices, and session records belonging to this user will be permanently erased from the database.
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setDeletingUser(null)}
                  disabled={savingDelete}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn-danger" 
                  onClick={handleConfirmDelete}
                  disabled={savingDelete}
                >
                  {savingDelete ? 'Deleting User...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
